import React, { useState, useEffect } from 'react';
import { Schedule, TimeSlot, Provider } from '../types';
import { schedulesAPI } from '../services/api';
import { Calendar, Clock, CheckCircle } from 'lucide-react';

interface ScheduleSelectionProps {
  provider: Provider;
  selectedDate: string;
  selectedTimeSlot: string;
  onSelectDate: (date: string) => void;
  onSelectTimeSlot: (timeSlot: string) => void;
}

const ScheduleSelection: React.FC<ScheduleSelectionProps> = ({
  provider,
  selectedDate,
  selectedTimeSlot,
  onSelectDate,
  onSelectTimeSlot
}) => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);

  useEffect(() => {
    fetchSchedules();
    generateAvailableDates();
  }, [provider._id || provider.id]);

  useEffect(() => {
    if (selectedDate) {
      fetchSchedules(selectedDate);
    }
  }, [selectedDate]);

  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    
    setAvailableDates(dates);
    
    // Auto-select tomorrow if no date is selected
    if (!selectedDate && dates?.length > 1) {
      onSelectDate(dates[1]);
    }
  };

  const fetchSchedules = async (date?: string) => {
    try {
      setLoading(true);
      const providerId = provider._id || provider.id;
      if (!providerId) {
        throw new Error('Provider ID is missing');
      }
      const response = await schedulesAPI.getProviderSchedules(providerId, {
        date: date || selectedDate
      });
      setSchedules(response.data.schedules);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setError('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getCurrentSchedule = () => {
    return schedules?.find(schedule => {
      const scheduleDate = new Date(schedule.date).toISOString().split('T')[0];
      return scheduleDate === selectedDate;
    });
  };

  const getAvailableTimeSlots = () => {
    const schedule = getCurrentSchedule();
    return schedule ? schedule.timeSlots?.filter(slot => !slot.isBooked && slot.isAvailable !== false) || [] : [];
  };

  if (loading && schedules?.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Select Date & Time</h3>

      {/* Date Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Choose a Date
        </label>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {availableDates?.map((date) => (
            <button
              key={date}
              onClick={() => onSelectDate(date)}
              className={`p-3 text-sm rounded-lg border transition-all ${
                selectedDate === date
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-white'
              }`}
            >
              <div className="text-xs font-medium">
                {formatDate(date)}
              </div>
              <div className="text-xs opacity-75 mt-1">
                {new Date(date).getDate()}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Time Slot Selection */}
      {selectedDate && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Available Time Slots for {formatDate(selectedDate)}
          </label>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => fetchSchedules()}
                className="px-4 py-2 bg-black text-white rounded-md hover:bg-black700"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {getAvailableTimeSlots()?.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    No available time slots for this date.
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Please select another date.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {getAvailableTimeSlots()?.map((slot, index) => {
                    const timeSlotString = `${slot.startTime}-${slot.endTime}`;
                    return (
                      <button
                        key={index}
                        onClick={() => onSelectTimeSlot(timeSlotString)}
                        className={`p-4 rounded-lg border transition-all ${
                          selectedTimeSlot === timeSlotString
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-center">
                          {selectedTimeSlot === timeSlotString && (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          <Clock className="h-4 w-4 mr-1" />
                          <span className="text-sm font-medium">
                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {selectedDate && selectedTimeSlot && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Selected: {formatDate(selectedDate)} at {selectedTimeSlot.split('-').map(time => formatTime(time)).join(' - ')}
              </p>
              <p className="text-xs text-green-600 mt-1">
                Provider: {provider.name} (₹{typeof provider.hourlyRate === 'number' ? provider.hourlyRate.toFixed(2) : provider.hourlyRate}/hour)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleSelection; 